import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LinkedAccountsPanel, SampleAccount } from '@/components/portfolio/LinkedAccountsPanel';
import { HoldingsTable } from '@/components/portfolio/HoldingsTable';
import { useHoldings } from '@/hooks/useHoldings';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Building2,
  PenLine,
  Plus,
  Loader2,
  DollarSign,
  Hash,
  Tag,
  Briefcase,
  PieChart,
} from 'lucide-react';
import { toast } from 'sonner';
import { Holding, AccountType, AssetClass } from '@/types/portfolio';

interface AccountsTabProps {
  sampleAccounts?: SampleAccount[];
}

const ACCOUNT_TYPES: AccountType[] = ['Taxable', 'Tax-Advantaged'];
const ASSET_CLASSES: AssetClass[] = ['US Stocks', 'Intl Stocks', 'Bonds', 'Commodities', 'Cash', 'Other'];

export function AccountsTab({ sampleAccounts }: AccountsTabProps) {
  const { holdings, addHolding, updateHolding, loading: holdingsLoading } = useHoldings();
  const [activeTab, setActiveTab] = useState<'linked' | 'manual'>('linked');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // New holding form state
  const [newHolding, setNewHolding] = useState({
    ticker: '',
    name: '',
    shares: '',
    currentPrice: '',
    costBasis: '',
    accountType: 'Taxable' as AccountType,
    assetClass: 'US Stocks' as AssetClass,
    expenseRatio: '',
  });

  const resetForm = () => {
    setNewHolding({
      ticker: '',
      name: '',
      shares: '',
      currentPrice: '',
      costBasis: '',
      accountType: 'Taxable',
      assetClass: 'US Stocks',
      expenseRatio: '',
    });
  };

  const handleAddHolding = async () => {
    if (!newHolding.ticker || !newHolding.shares || !newHolding.currentPrice) {
      toast.error('Please fill in ticker, shares, and current price');
      return;
    }

    setIsAdding(true);
    try {
      await addHolding({
        ticker: newHolding.ticker.toUpperCase(),
        name: newHolding.name || newHolding.ticker.toUpperCase(),
        shares: parseFloat(newHolding.shares),
        currentPrice: parseFloat(newHolding.currentPrice),
        costBasis: newHolding.costBasis ? parseFloat(newHolding.costBasis) : parseFloat(newHolding.currentPrice),
        accountType: newHolding.accountType,
        assetClass: newHolding.assetClass,
        expenseRatio: newHolding.expenseRatio ? parseFloat(newHolding.expenseRatio) / 100 : undefined,
      });

      toast.success(`Added ${newHolding.ticker.toUpperCase()} to your portfolio`);
      resetForm();
      setAddDialogOpen(false);
    } catch {
      toast.error('Failed to add holding');
    } finally {
      setIsAdding(false);
    }
  };

  const handleUpdateHoldings = async (updatedHoldings: Holding[]) => {
    // Find what changed
    for (const updated of updatedHoldings) {
      const original = holdings.find((h) => h.id === updated.id);
      if (original) {
        // Check if anything changed
        const hasChanges =
          original.shares !== updated.shares ||
          original.currentPrice !== updated.currentPrice ||
          original.costBasis !== updated.costBasis;

        if (hasChanges) {
          await updateHolding(updated.id, {
            shares: updated.shares,
            currentPrice: updated.currentPrice,
            costBasis: updated.costBasis,
          });
        }
      }
    }
  };

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold">Linked Accounts</h1>
        <p className="text-sm text-muted-foreground">
          Connect your brokerage accounts or add holdings manually
        </p>
      </div>

      {/* Account Type Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'linked' | 'manual')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="linked" className="gap-2">
            <Building2 className="h-4 w-4" />
            Plaid Accounts
          </TabsTrigger>
          <TabsTrigger value="manual" className="gap-2">
            <PenLine className="h-4 w-4" />
            Manual Holdings
          </TabsTrigger>
        </TabsList>

        {/* Plaid Accounts Tab */}
        <TabsContent value="linked" className="mt-4 space-y-4">
          <LinkedAccountsPanel sampleAccounts={sampleAccounts} />

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">How Plaid Works</CardTitle>
              <CardDescription>
                Securely connect your brokerage accounts for automatic portfolio syncing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">1</span>
                  </div>
                  <h4 className="font-medium text-sm">Connect</h4>
                  <p className="text-xs text-muted-foreground">
                    Click "Link Account" and securely sign in to your brokerage
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">2</span>
                  </div>
                  <h4 className="font-medium text-sm">Sync</h4>
                  <p className="text-xs text-muted-foreground">
                    Your holdings are automatically imported and kept up to date
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">3</span>
                  </div>
                  <h4 className="font-medium text-sm">Analyze</h4>
                  <p className="text-xs text-muted-foreground">
                    Get insights on your complete portfolio across all accounts
                  </p>
                </div>
              </div>

              <div className="p-4 rounded-lg border border-primary/20 bg-primary/5 space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <span className="text-lg">🔒</span>
                  Bank-Level Security
                </h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Your login credentials are never stored on our servers</li>
                  <li>• Read-only access - we can never move your money</li>
                  <li>• Data encrypted with 256-bit SSL</li>
                  <li>• Plaid is used by Venmo, Robinhood, and thousands of apps</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Manual Holdings Tab */}
        <TabsContent value="manual" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Manual Holdings</CardTitle>
                  <CardDescription>
                    Add or edit holdings that aren't connected via Plaid
                  </CardDescription>
                </div>
                <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="gap-2">
                      <Plus className="h-4 w-4" />
                      Add Holding
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Add New Holding</DialogTitle>
                      <DialogDescription>
                        Enter the details for your investment holding
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                      {/* Ticker and Name */}
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="ticker" className="flex items-center gap-2">
                            <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                            Ticker Symbol *
                          </Label>
                          <Input
                            id="ticker"
                            value={newHolding.ticker}
                            onChange={(e) =>
                              setNewHolding({ ...newHolding, ticker: e.target.value.toUpperCase() })
                            }
                            placeholder="e.g., VTI"
                            className="uppercase"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="name" className="flex items-center gap-2">
                            <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />
                            Name (optional)
                          </Label>
                          <Input
                            id="name"
                            value={newHolding.name}
                            onChange={(e) => setNewHolding({ ...newHolding, name: e.target.value })}
                            placeholder="e.g., Vanguard Total Stock Market"
                          />
                        </div>
                      </div>

                      {/* Shares and Price */}
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="shares" className="flex items-center gap-2">
                            <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                            Number of Shares *
                          </Label>
                          <Input
                            id="shares"
                            type="number"
                            step="0.0001"
                            value={newHolding.shares}
                            onChange={(e) => setNewHolding({ ...newHolding, shares: e.target.value })}
                            placeholder="100"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="price" className="flex items-center gap-2">
                            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                            Current Price *
                          </Label>
                          <Input
                            id="price"
                            type="number"
                            step="0.01"
                            value={newHolding.currentPrice}
                            onChange={(e) =>
                              setNewHolding({ ...newHolding, currentPrice: e.target.value })
                            }
                            placeholder="250.00"
                          />
                        </div>
                      </div>

                      {/* Cost Basis and Expense Ratio */}
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="costBasis" className="flex items-center gap-2">
                            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                            Cost Basis (optional)
                          </Label>
                          <Input
                            id="costBasis"
                            type="number"
                            step="0.01"
                            value={newHolding.costBasis}
                            onChange={(e) =>
                              setNewHolding({ ...newHolding, costBasis: e.target.value })
                            }
                            placeholder="Same as current price"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="expenseRatio" className="flex items-center gap-2">
                            <PieChart className="h-3.5 w-3.5 text-muted-foreground" />
                            Expense Ratio % (optional)
                          </Label>
                          <Input
                            id="expenseRatio"
                            type="number"
                            step="0.01"
                            value={newHolding.expenseRatio}
                            onChange={(e) =>
                              setNewHolding({ ...newHolding, expenseRatio: e.target.value })
                            }
                            placeholder="0.03"
                          />
                        </div>
                      </div>

                      {/* Account Type and Asset Class */}
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="accountType">Account Type</Label>
                          <Select
                            value={newHolding.accountType}
                            onValueChange={(v) =>
                              setNewHolding({ ...newHolding, accountType: v as AccountType })
                            }
                          >
                            <SelectTrigger id="accountType">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ACCOUNT_TYPES.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="assetClass">Asset Class</Label>
                          <Select
                            value={newHolding.assetClass}
                            onValueChange={(v) =>
                              setNewHolding({ ...newHolding, assetClass: v as AssetClass })
                            }
                          >
                            <SelectTrigger id="assetClass">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ASSET_CLASSES.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button variant="ghost" onClick={() => setAddDialogOpen(false)} disabled={isAdding}>
                        Cancel
                      </Button>
                      <Button onClick={handleAddHolding} disabled={isAdding}>
                        {isAdding ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Adding...
                          </>
                        ) : (
                          'Add Holding'
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {holdingsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : holdings.length === 0 ? (
                <div className="text-center py-8 space-y-3">
                  <div className="mx-auto w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center">
                    <PenLine className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">No holdings yet</p>
                    <p className="text-xs text-muted-foreground">
                      Add your first holding to start tracking your portfolio
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAddDialogOpen(true)}
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Your First Holding
                  </Button>
                </div>
              ) : (
                <HoldingsTable holdings={holdings} onUpdate={handleUpdateHoldings} />
              )}
            </CardContent>
          </Card>

          {/* Tips Card */}
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-4">
              <h4 className="font-medium text-sm mb-2">Tips for Manual Entry</h4>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Use the ticker symbol (e.g., VTI, AAPL, BND) for accurate tracking</li>
                <li>• Cost basis is your original purchase price per share</li>
                <li>• Expense ratio is the annual fee charged by ETFs/mutual funds</li>
                <li>• You can edit any holding by clicking on the table cells</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
